import React, { useEffect, useState } from "react";
import { api, fmtEuro, fmtNum, todayISO } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Wrench, Trash2, Save, AlertTriangle, CheckCircle2, Loader2, Gauge } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { ChartTooltip } from "@/components/ChartTooltip";

const VEHICLE_IMG = "https://images.unsplash.com/photo-1783862303922-9eb63d574545";

const MAINT_LABELS = {
  vidange: "Vidange",
  revision: "Révision",
  pneus: "Pneus",
  freins: "Freins",
  controle_technique: "Contrôle technique",
  autre: "Autre",
};

export default function Vehicule() {
  const [vehicle, setVehicle] = useState(null);
  const [maint, setMaint] = useState([]);
  const [saving, setSaving] = useState(false);
  const [openMaint, setOpenMaint] = useState(false);
  const [maintForm, setMaintForm] = useState({ date: todayISO(), type: "vidange", km: "", cost: "", notes: "" });

  const [emptyKm, setEmptyKm] = useState([]);
  const [openEmptyKm, setOpenEmptyKm] = useState(false);
  const [emptyKmForm, setEmptyKmForm] = useState({ date: todayISO(), km: "", notes: "" });
  const [kmPeriod, setKmPeriod] = useState("week");
  const [kmSummary, setKmSummary] = useState([]);

  const load = async () => {
    const [v, m, ek] = await Promise.all([api.get("/vehicle"), api.get("/maintenance"), api.get("/empty-km")]);
    setVehicle(v.data);
    setMaint(m.data);
    setEmptyKm(ek.data);
  };
  useEffect(() => { load(); }, []);

  const loadKmSummary = async (period) => {
    const r = await api.get(`/vehicle/km-summary?period=${period}&count=12`);
    setKmSummary(r.data);
  };
  useEffect(() => { loadKmSummary(kmPeriod); }, [kmPeriod]);

  const saveVehicle = async () => {
    setSaving(true);
    try {
      await api.put("/vehicle", {
        ...vehicle,
        year: Number(vehicle.year) || 2024,
        current_km: Number(vehicle.current_km) || 0,
        next_vidange_km: vehicle.next_vidange_km ? Number(vehicle.next_vidange_km) : null,
        next_revision_km: vehicle.next_revision_km ? Number(vehicle.next_revision_km) : null,
        credit_monthly: Number(vehicle.credit_monthly) || 0,
        insurance_monthly: Number(vehicle.insurance_monthly) || 0,
      });
      toast.success("Véhicule mis à jour");
      load();
    } catch (e) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const addMaint = async (e) => {
    e.preventDefault();
    await api.post("/maintenance", {
      ...maintForm,
      km: maintForm.km ? Number(maintForm.km) : null,
      cost: Number(maintForm.cost) || 0,
    });
    toast.success("Entretien enregistré");
    setOpenMaint(false);
    setMaintForm({ date: todayISO(), type: "vidange", km: "", cost: "", notes: "" });
    load();
  };

  const removeMaint = async (id) => {
    if (!window.confirm("Supprimer cet entretien ?")) return;
    await api.delete(`/maintenance/${id}`);
    load();
  };

  const addEmptyKm = async (e) => {
    e.preventDefault();
    if (!emptyKmForm.km || Number(emptyKmForm.km) <= 0) {
      toast.error("Indique un kilométrage à vide valide");
      return;
    }
    await api.post("/empty-km", { ...emptyKmForm, km: Number(emptyKmForm.km) });
    toast.success("Kilométrage à vide enregistré");
    setOpenEmptyKm(false);
    setEmptyKmForm({ date: todayISO(), km: "", notes: "" });
    load();
    loadKmSummary(kmPeriod);
  };

  const removeEmptyKm = async (id) => {
    if (!window.confirm("Supprimer cette entrée ?")) return;
    await api.delete(`/empty-km/${id}`);
    load();
    loadKmSummary(kmPeriod);
  };

  if (!vehicle) return <div className="text-muted-foreground">Chargement…</div>;

  // Alerts
  const alerts = [];
  const today = new Date();
  const daysUntil = (iso) => {
    if (!iso) return null;
    return Math.floor((new Date(iso) - today) / (1000 * 60 * 60 * 24));
  };
  const kmToNext = (target) => (target ? Number(target) - Number(vehicle.current_km || 0) : null);

  const ctDays = daysUntil(vehicle.next_ct_date);
  if (ctDays !== null && ctDays < 60) alerts.push({ label: `Contrôle technique dans ${ctDays} j`, level: ctDays < 15 ? "danger" : "warn" });
  const insDays = daysUntil(vehicle.insurance_expiry);
  if (insDays !== null && insDays < 45) alerts.push({ label: `Assurance à renouveler dans ${insDays} j`, level: insDays < 15 ? "danger" : "warn" });
  const cgDays = daysUntil(vehicle.carte_grise_expiry);
  if (cgDays !== null && cgDays < 45) alerts.push({ label: `Carte grise expire dans ${cgDays} j`, level: cgDays < 15 ? "danger" : "warn" });
  const vidKm = kmToNext(vehicle.next_vidange_km);
  if (vidKm !== null && vidKm < 2000) alerts.push({ label: `Vidange dans ${vidKm} km`, level: vidKm < 500 ? "danger" : "warn" });
  const revKm = kmToNext(vehicle.next_revision_km);
  if (revKm !== null && revKm < 5000) alerts.push({ label: `Révision dans ${revKm} km`, level: revKm < 1000 ? "danger" : "warn" });

  return (
    <div className="space-y-6">
      <div>
        <div className="overline mb-2">Véhicule</div>
        <h1 className="font-manrope text-3xl md:text-4xl font-extrabold">Gestion du véhicule</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Hero card */}
        <div className="lg:col-span-2 card-surface overflow-hidden">
          <div className="relative h-40 md:h-56 bg-cover bg-center" style={{ backgroundImage: `linear-gradient(to top, hsl(var(--card)) 5%, transparent 80%), url(${VEHICLE_IMG})` }}>
            <div className="absolute bottom-4 left-4 right-4">
              <div className="overline mb-1">Mon véhicule</div>
              <div className="font-manrope font-extrabold text-xl md:text-2xl">{vehicle.make} {vehicle.model}</div>
              <div className="text-xs text-muted-foreground">Mise en circulation : {vehicle.mise_en_circulation}</div>
            </div>
          </div>
          <div className="p-4 md:p-6 grid grid-cols-2 md:grid-cols-3 gap-4">
            <Field label="Marque"><Input value={vehicle.make} onChange={(e) => setVehicle({ ...vehicle, make: e.target.value })} data-testid="input-vehicle-make" /></Field>
            <Field label="Modèle" full><Input value={vehicle.model} onChange={(e) => setVehicle({ ...vehicle, model: e.target.value })} data-testid="input-vehicle-model" /></Field>
            <Field label="Année"><Input type="number" value={vehicle.year} onChange={(e) => setVehicle({ ...vehicle, year: e.target.value })} data-testid="input-vehicle-year" /></Field>
            <Field label="Immatriculation"><Input value={vehicle.plate || ""} onChange={(e) => setVehicle({ ...vehicle, plate: e.target.value })} data-testid="input-vehicle-plate" /></Field>
            <Field label="Mise en circulation"><Input type="date" value={vehicle.mise_en_circulation || ""} onChange={(e) => setVehicle({ ...vehicle, mise_en_circulation: e.target.value })} data-testid="input-vehicle-mec" /></Field>
            <Field label="Kilométrage actuel"><Input type="number" value={vehicle.current_km} onChange={(e) => setVehicle({ ...vehicle, current_km: e.target.value })} data-testid="input-vehicle-km" /></Field>
            <Field label="Prochaine vidange (km)"><Input type="number" value={vehicle.next_vidange_km || ""} onChange={(e) => setVehicle({ ...vehicle, next_vidange_km: e.target.value })} data-testid="input-vehicle-nvid" /></Field>
            <Field label="Prochaine révision (km)"><Input type="number" value={vehicle.next_revision_km || ""} onChange={(e) => setVehicle({ ...vehicle, next_revision_km: e.target.value })} data-testid="input-vehicle-nrev" /></Field>
            <Field label="Contrôle technique"><Input type="date" value={vehicle.next_ct_date || ""} onChange={(e) => setVehicle({ ...vehicle, next_ct_date: e.target.value })} data-testid="input-vehicle-ct" /></Field>
            <Field label="Fin assurance"><Input type="date" value={vehicle.insurance_expiry || ""} onChange={(e) => setVehicle({ ...vehicle, insurance_expiry: e.target.value })} data-testid="input-vehicle-ins" /></Field>
            <Field label="Fin carte grise"><Input type="date" value={vehicle.carte_grise_expiry || ""} onChange={(e) => setVehicle({ ...vehicle, carte_grise_expiry: e.target.value })} data-testid="input-vehicle-cg" /></Field>
            <Field label="Crédit / mois (€)"><Input type="number" step="0.01" value={vehicle.credit_monthly} onChange={(e) => setVehicle({ ...vehicle, credit_monthly: e.target.value })} data-testid="input-vehicle-credit" /></Field>
            <Field label="Assurance / mois (€)"><Input type="number" step="0.01" value={vehicle.insurance_monthly} onChange={(e) => setVehicle({ ...vehicle, insurance_monthly: e.target.value })} data-testid="input-vehicle-insmonth" /></Field>
            <div className="md:col-span-3 flex justify-end">
              <Button onClick={saveVehicle} disabled={saving} data-testid="btn-save-vehicle">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Enregistrer
              </Button>
            </div>
          </div>
        </div>

        {/* Alerts */}
        <div className="card-surface p-5 space-y-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-primary" />
            <h3 className="font-manrope font-semibold">Alertes automatiques</h3>
          </div>
          {alerts.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" />
              Aucune alerte, tout est à jour.
            </div>
          ) : (
            <div className="space-y-2">
              {alerts.map((a, i) => (
                <div
                  key={i}
                  className={`text-sm rounded-md px-3 py-2 border ${
                    a.level === "danger"
                      ? "bg-destructive/10 border-destructive/25 text-destructive"
                      : "bg-primary/10 border-primary/25 text-primary"
                  }`}
                  data-testid={`alert-${i}`}
                >
                  {a.label}
                </div>
              ))}
            </div>
          )}
          <div className="pt-3 border-t border-border/60">
            <div className="text-xs text-muted-foreground mb-2">Kilométrage actuel</div>
            <div className="font-manrope font-extrabold text-2xl num">{fmtNum(vehicle.current_km, 0)} km</div>
          </div>
        </div>
      </div>

      {/* Maintenance history */}
      <div className="card-surface p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Wrench className="h-4 w-4 text-primary" />
            <h3 className="font-manrope font-semibold">Historique entretien</h3>
          </div>
          <Dialog open={openMaint} onOpenChange={setOpenMaint}>
            <DialogTrigger asChild>
              <Button size="sm" variant="secondary" data-testid="btn-add-maintenance">
                <Plus className="h-4 w-4 mr-1" /> Ajouter
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-manrope">Nouvel entretien</DialogTitle>
              </DialogHeader>
              <form onSubmit={addMaint} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Date"><Input type="date" required value={maintForm.date} onChange={(e) => setMaintForm({ ...maintForm, date: e.target.value })} data-testid="input-maint-date" /></Field>
                  <Field label="Type">
                    <Select value={maintForm.type} onValueChange={(v) => setMaintForm({ ...maintForm, type: v })}>
                      <SelectTrigger data-testid="select-maint-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(MAINT_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Kilométrage"><Input type="number" value={maintForm.km} onChange={(e) => setMaintForm({ ...maintForm, km: e.target.value })} data-testid="input-maint-km" /></Field>
                  <Field label="Coût (€)"><Input type="number" step="0.01" value={maintForm.cost} onChange={(e) => setMaintForm({ ...maintForm, cost: e.target.value })} data-testid="input-maint-cost" /></Field>
                </div>
                <Field label="Notes"><Textarea rows={2} value={maintForm.notes} onChange={(e) => setMaintForm({ ...maintForm, notes: e.target.value })} data-testid="input-maint-notes" /></Field>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setOpenMaint(false)}>Annuler</Button>
                  <Button type="submit" data-testid="btn-save-maintenance">Enregistrer</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        {maint.length === 0 ? (
          <div className="text-sm text-muted-foreground">Aucune intervention enregistrée.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left px-2 py-2">Date</th>
                <th className="text-left px-2 py-2">Type</th>
                <th className="text-right px-2 py-2">Km</th>
                <th className="text-right px-2 py-2">Coût</th>
                <th className="text-left px-2 py-2">Notes</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody data-testid="maintenance-tbody">
              {maint.map((m) => (
                <tr key={m.id} className="border-b border-border/60 hover:bg-muted/40 transition-colors">
                  <td className="px-2 py-2 num">{m.date}</td>
                  <td className="px-2 py-2">{MAINT_LABELS[m.type] || m.type}</td>
                  <td className="px-2 py-2 text-right num">{m.km ? fmtNum(m.km) : "—"}</td>
                  <td className="px-2 py-2 text-right num">{fmtEuro(m.cost)}</td>
                  <td className="px-2 py-2 text-muted-foreground text-xs">{m.notes || "—"}</td>
                  <td className="px-2 py-2 text-right">
                    <button onClick={() => removeMaint(m.id)} className="text-muted-foreground hover:text-destructive" data-testid={`btn-delete-maint-${m.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Kilométrage — évolution + saisie à vide */}
      <div className="card-surface p-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-2">
            <Gauge className="h-4 w-4 text-primary" />
            <h3 className="font-manrope font-semibold">Évolution du kilométrage</h3>
          </div>
          <div className="flex items-center gap-2">
            <Select value={kmPeriod} onValueChange={setKmPeriod}>
              <SelectTrigger className="w-36" data-testid="select-km-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="week">Par semaine</SelectItem>
                <SelectItem value="month">Par mois</SelectItem>
              </SelectContent>
            </Select>
            <Dialog open={openEmptyKm} onOpenChange={setOpenEmptyKm}>
              <DialogTrigger asChild>
                <Button size="sm" variant="secondary" data-testid="btn-add-empty-km">
                  <Plus className="h-4 w-4 mr-1" /> Km à vide
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-manrope">Ajouter des km à vide</DialogTitle>
                </DialogHeader>
                <form onSubmit={addEmptyKm} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Date"><Input type="date" required value={emptyKmForm.date} onChange={(e) => setEmptyKmForm({ ...emptyKmForm, date: e.target.value })} data-testid="input-emptykm-date" /></Field>
                    <Field label="Km à vide"><Input type="number" step="0.1" min="0" required value={emptyKmForm.km} onChange={(e) => setEmptyKmForm({ ...emptyKmForm, km: e.target.value })} data-testid="input-emptykm-km" /></Field>
                  </div>
                  <Field label="Notes"><Textarea rows={2} placeholder="Ex : trajets à vide entre les courses, retour domicile…" value={emptyKmForm.notes} onChange={(e) => setEmptyKmForm({ ...emptyKmForm, notes: e.target.value })} data-testid="input-emptykm-notes" /></Field>
                  <DialogFooter>
                    <Button type="button" variant="ghost" onClick={() => setOpenEmptyKm(false)}>Annuler</Button>
                    <Button type="submit" data-testid="btn-save-empty-km">Enregistrer</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="h-64 mb-6">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={kmSummary}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="period" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
              <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
              <Tooltip content={<ChartTooltip valueFormatter={(v) => `${fmtNum(v, 1)} km`} />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="km_courses" name="Km courses" stackId="km" fill="hsl(var(--chart-1))" radius={[0, 0, 0, 0]} />
              <Bar dataKey="km_vide" name="Km à vide" stackId="km" fill="hsl(var(--chart-3))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {emptyKm.length === 0 ? (
          <div className="text-sm text-muted-foreground">Aucun kilométrage à vide enregistré.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left px-2 py-2">Date</th>
                <th className="text-right px-2 py-2">Km à vide</th>
                <th className="text-left px-2 py-2">Notes</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody data-testid="empty-km-tbody">
              {emptyKm.map((k) => (
                <tr key={k.id} className="border-b border-border/60 hover:bg-muted/40 transition-colors">
                  <td className="px-2 py-2 num">{k.date}</td>
                  <td className="px-2 py-2 text-right num">{fmtNum(k.km, 1)} km</td>
                  <td className="px-2 py-2 text-muted-foreground text-xs">{k.notes || "—"}</td>
                  <td className="px-2 py-2 text-right">
                    <button onClick={() => removeEmptyKm(k.id)} className="text-muted-foreground hover:text-destructive" data-testid={`btn-delete-emptykm-${k.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={`space-y-1.5 ${full ? "md:col-span-2" : ""}`}>
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
